export default class ShakeDetector {
  static #permissionGranted: boolean | null = null

  threshold: number
  timeout: number
  onShake: () => void
  lastX: number
  lastY: number
  lastZ: number
  lastUpdate: number
  shakeTimeout: ReturnType<typeof setTimeout> | null

  constructor(options: { threshold?: number; timeout?: number; onShake?: () => void } = {}) {
    this.threshold = options.threshold || 4
    this.timeout = options.timeout || 1000
    this.onShake = options.onShake || (() => {})

    this.lastX = 0
    this.lastY = 0
    this.lastZ = 0
    this.lastUpdate = 0
    this.shakeTimeout = null

    this.handleShake = this.handleShake.bind(this)
  }

  static async requestPermission() {
    if (this.#permissionGranted !== null) {
      return this.#permissionGranted
    }

    if (!window.DeviceMotionEvent) {
      this.#permissionGranted = false
      return false
    }

    try {
      const deviceMotionCtor = DeviceMotionEvent as typeof DeviceMotionEvent & {
        requestPermission?: () => Promise<'granted' | 'denied'>
      }
      if (typeof deviceMotionCtor.requestPermission === 'function') {
        const permission = await deviceMotionCtor.requestPermission()
        this.#permissionGranted = permission === 'granted'
        return this.#permissionGranted
      }

      this.#permissionGranted = true
      return true
    } catch (err) {
      console.error('Ошибка при запросе разрешения:', err)
      this.#permissionGranted = false
      return false
    }
  }

  static get hasPermission() {
    return this.#permissionGranted
  }

  static async hasAccelerometer() {
    if (!window.DeviceMotionEvent) return false

    return new Promise<boolean>(resolve => {
      const timeout = setTimeout(() => {
        window.removeEventListener('devicemotion', checkMotion)
        resolve(false)
      }, 1000)

      const checkMotion = (event: DeviceMotionEvent) => {
        const acceleration = event.accelerationIncludingGravity
        const hasData =
          !!acceleration && (acceleration.x !== null || acceleration.y !== null || acceleration.z !== null)

        window.removeEventListener('devicemotion', checkMotion)
        clearTimeout(timeout)
        resolve(hasData)
      }

      window.addEventListener('devicemotion', checkMotion, false)
    })
  }
  start() {
    const deviceMotionCtor = DeviceMotionEvent as typeof DeviceMotionEvent & {
      requestPermission?: () => Promise<'granted' | 'denied'>
    }

    if (typeof deviceMotionCtor.requestPermission === 'function') {
      deviceMotionCtor
        .requestPermission()
        .then((permissionState: 'granted' | 'denied') => {
          if (permissionState === 'granted') {
            this.addListener()
          }
        })
        .catch(console.error)
    } else {
      this.addListener()
    }
  }

  stop() {
    window.removeEventListener('devicemotion', this.handleShake, true)
  }

  addListener() {
    window.addEventListener('devicemotion', this.handleShake, true)
  }

  handleShake(event: DeviceMotionEvent) {
    const current = event.accelerationIncludingGravity
    if (!current) return

    const currentTime = new Date().getTime()
    const timeDiff = currentTime - this.lastUpdate

    if (timeDiff > 100) {
      const diffTime = currentTime - this.lastUpdate
      this.lastUpdate = currentTime

      const speed =
        (Math.abs((current.x ?? 0) + (current.y ?? 0) + (current.z ?? 0) - this.lastX - this.lastY - this.lastZ) /
          diffTime) *
        10000

      if (speed / 1000 > this.threshold) {
        if (!this.shakeTimeout) {
          this.shakeTimeout = setTimeout(() => {
            this.shakeTimeout = null
          }, this.timeout)

          this.onShake()
        }
      }

      this.lastX = current.x ?? 0
      this.lastY = current.y ?? 0
      this.lastZ = current.z ?? 0
    }
  }
}
